import { apiRequest } from "@/lib/api";

export type ReconciliationResult = "Matched" | "Mismatch" | "Needs Review";

export type ReconciliationActivityRow = {
  id: string;
  studentId: string;
  student: string;
  email: string;
  phone: string;
  program: string;
  programCode: string;
  plan: string;
  planType: string;
  paymentMethod: string;
  cardMasked: string;
  cardType: string;
  totalInstallments: number;
  enrollment: string;
  invoice: string;
  amount: number;
  type: string;
  school: string;
  square: string;
  date: string;
  squareDate: string;
  due: string;
  txn: string;
  result: ReconciliationResult;
  installmentNo: number | null;
  reason: string;
  action: string;
  orderId: string | null;
  lateFeeApplied?: boolean;
  lateFeeAmount?: number;
  lateFeeStatus?: "APPLIED" | "PENDING" | "NOT_APPLIED" | "IN_GRACE";
  amountWithLateFee?: number;
  inLateFeeGracePeriod?: boolean;
  lateFeeGraceDaysRemaining?: number;
};

export type ReconciliationAnalytics = {
  processed: { amount: number; count: number };
  due: { amount: number; count: number };
  upcoming: { amount: number; count: number };
  refunds: { amount: number; count: number };
};

export type StudentProgramReconciliation = {
  student: { name: string; email: string; phone: string };
  programs: Array<{
    program: string;
    programCode: string;
    enrollment: string;
    plan: string;
    planType: string;
    paymentMethod: string;
    cardMasked: string;
    cardType: string;
    totalInstallments: number;
    total: number;
    paid: number;
    outstanding: number;
    remaining: number;
    nextPayment: string;
    nextAmount: number;
    status: "In Progress" | "Needs Review";
    records: ReconciliationActivityRow[];
  }>;
};

export type BalanceReportRow = {
  period: string;
  systemGross: number;
  systemRefunds: number;
  feePercent: number;
  perChargeFee: number;
  chargeCount: number;
  expectedSquare: number;
  actualSquareEntered: number | null;
  squareRefundEntered: number;
  difference: number | null;
  status: "Matched" | "Difference Found" | "Awaiting Entry";
};

export const paymentReconciliationApi = {
  getActivity: (params: {
    analyticsDate?: "month" | "week" | "year" | "custom";
    start?: string;
    end?: string;
    program?: string;
    paymentType?: string;
    schoolStatus?: string;
    result?: string;
    search?: string;
    schoolStatusFilter?: string;
    squareStatusFilter?: string;
    resultFilter?: string;
    activeMetric?: "processed" | "due" | "upcoming" | "refunds" | "";
    page?: number;
    limit?: number;
  } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") q.append(key, String(value));
    });
    const qs = q.toString();
    return apiRequest<{
      success: boolean;
      data: {
        rows: ReconciliationActivityRow[];
        total: number;
        analytics: ReconciliationAnalytics;
      };
    }>(`/api/admin/auth/payments/reconciliation/activity${qs ? `?${qs}` : ""}`);
  },

  getAlerts: () =>
    apiRequest<{
      success: boolean;
      data: { rows: ReconciliationActivityRow[]; total: number };
    }>("/api/admin/auth/payments/reconciliation/alerts"),

  getStudent: (search: string) =>
    apiRequest<{
      success: boolean;
      data: StudentProgramReconciliation;
    }>(
      `/api/admin/auth/payments/reconciliation/student?search=${encodeURIComponent(search)}`
    ),

  getBalance: (params: {
    reportType?: "month" | "year" | "custom";
    month?: string;
    start?: string;
    end?: string;
    feePercent?: number;
    perTransactionFee?: number;
    actualSquareCollected?: number | null;
    actualSquareRefund?: number;
  } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        q.append(key, String(value));
      }
    });
    const qs = q.toString();
    return apiRequest<{
      success: boolean;
      data: {
        summary: {
          systemGross: number;
          expectedSquare: number;
          actualSquare: number | null;
          difference: number | null;
        };
        rows: BalanceReportRow[];
      };
    }>(`/api/admin/auth/payments/reconciliation/balance${qs ? `?${qs}` : ""}`);
  },
};
